// src/components/TheAppBar

import React, { useState } from 'react';
import {
  AppBar, Toolbar, Tabs, Tab, Box, IconButton, useMediaQuery, useTheme,
  Drawer, // Import Drawer
  List, // Import List components for Drawer content
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon, // To add icons later
  Divider, // Optional: for visual separation
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close'; // Icon for closing the drawer
// Import specific icons for menu items (replace with your actual icons)
import HomeIcon from '@mui/icons-material/Home'; // Example Icon
import TimelineIcon from '@mui/icons-material/Timeline'; // Example Icon
import ForumIcon from '@mui/icons-material/Forum'; // Example Icon
import ScoreboardIcon from '@mui/icons-material/Scoreboard'; // Example Icon
import InfoIcon from '@mui/icons-material/Info'; // Example Icon
import ArticleIcon from '@mui/icons-material/Article'; // Example Icon
import HandshakeIcon from '@mui/icons-material/Handshake'; // Example Icon

import UserMenu from './UserMenu';

// Define tab paths centrally - Updated order with Icons
const tabsData = [
  { label: "Invitations", path: '/', icon: <HomeIcon /> },
  { label: "Timelines", path: '/timelines', icon: <TimelineIcon /> },
  { label: "Forums", path: '/forums', icon: <ForumIcon /> },
  { label: "CRS Score", path: '/crs-score', icon: <ScoreboardIcon /> },
  { label: "IRCC Info", path: '/ircc-info', icon: <InfoIcon /> },
  { label: "Blog", path: '/blog', icon: <ArticleIcon /> },
  { label: "Partners", path: '/partners', icon: <HandshakeIcon /> },
];

export const TheAppBar = ({ tabValue, showTabs = true, scrollOpacity = 0 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Removed internal scrollOpacity state and useEffect

  // State for managing the Drawer's open/close status
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handler to close mobile drawer when UserMenu is clicked
  const handleUserMenuClick = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  // Drawer content
  const drawer = (
    <Box
      sx={{ width: 250, display: 'flex', flexDirection: 'column', height: '100%' }} // Adjust width and make it flex column
      role="presentation"
      // onClick={handleDrawerToggle} // Remove this to allow interaction with UserMenu
      onKeyDown={handleDrawerToggle} // Optional: Close drawer on escape key
    >
      {/* Optional: Add a header to the Drawer */}
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--color-surface-variant)', color: 'var(--color-on-surface-variant)' }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
            {/* You could put a smaller logo or just text here */}
            Menu
         </Box>
         <IconButton onClick={handleDrawerToggle}>
           <CloseIcon />
         </IconButton>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }} onClick={handleDrawerToggle}> {/* Close drawer on item click for list items */}
        {tabsData.map((tab, index) => (
          <ListItem key={tab.label} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={tab.path}
              selected={tabValue === index} // Highlight selected item
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'var(--color-primary-container)', // Use theme colors
                  color: 'var(--color-on-primary-container)',
                  '& .MuiListItemIcon-root': {
                     color: 'var(--color-on-primary-container)',
                  }
                },
                '&:hover': {
                   backgroundColor: 'rgba(0, 0, 0, 0.04)', // Subtle hover
                },
                paddingY: 1.5, // Increase vertical padding
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'var(--color-on-surface-variant)' }}> {/* Style icon color */}
                {tab.icon}
              </ListItemIcon>
              <ListItemText primary={tab.label} primaryTypographyProps={{ fontWeight: 500 }} /> {/* Make text slightly bolder */}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
       <Divider sx={{ my: 1 }}/>
        {/* UserMenu at the bottom of the drawer */}
        <Box sx={{ p: 2, mt: 'auto' }}> {/* Push UserMenu to the bottom */}
          <UserMenu onMenuClick={handleUserMenuClick} isMobileDrawer={true} />
        </Box>
    </Box>
  );

  return (
    <> {/* Need React Fragment because Drawer is sibling to AppBar */}
      <AppBar
        position="sticky"
        elevation={2}
        sx={{
          top: 0,
          // AppBar is now opaque for better visibility.
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-on-surface)',
          borderBottom: '1px solid var(--color-outline-variant)',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: { xs: 56, sm: 64 },
            px: 2,
          }}
        >
          {/* Left: Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <img
                src="/assets/TrackCOPR_Logo_1500x1024.png"
                alt="TrackCOPR Logo"
                style={{ height: 40, width: 'auto', marginRight: 12, objectFit: 'contain' }}
                loading="lazy"
              />
            </RouterLink>
          </Box>

          {/* Center: Tabs or Mobile Menu Trigger */}
          {showTabs && (
            isMobile ? (
              // Mobile: Menu Icon to toggle Drawer
              <>
                 <Box sx={{ flexGrow: 1 }} /> {/* Pushes menu icon to the right before UserMenu */}
                 <IconButton
                   size="large"
                   edge="end"
                   color="inherit"
                   aria-label="menu"
                   onClick={handleDrawerToggle} // Use the new handler
                   sx={{ display: { xs: 'flex', md: 'none' } }}
                 >
                   <MenuIcon />
                 </IconButton>
              </>
            ) : (
              // Desktop: Tabs
              <Tabs
                value={tabValue}
                textColor="inherit"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minWidth: 0,
                  flexGrow: 1,
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  '& .MuiTabs-scrollButtons': {
                    '&.Mui-disabled': { opacity: 0.3 },
                  },
                }}
              >
                {/* Use only label for Tabs, icons might be too much here */}
                {tabsData.map((tab, index) => (
                   <Tab
                     key={tab.label}
                     label={tab.label}
                     component={RouterLink}
                     to={tab.path}
                     value={index}
                   />
                ))}
              </Tabs>
            )
          )}

          {/* Right: User controls - Conditionally render UserMenu for non-mobile */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <UserMenu onMenuClick={handleUserMenuClick} />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary" // Slides over content, disappears when closed
        anchor="right" // Slide in from the right
        open={mobileOpen}
        onClose={handleDrawerToggle} // Close when clicking outside the drawer
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' }, // Show only on mobile/tablet
          '& .MuiDrawer-paper': {
             boxSizing: 'border-box',
             width: 250, // Match the Box width inside
             backgroundColor: 'var(--color-surface)', // Match AppBar or choose another theme color
             color: 'var(--color-on-surface)', // Text color inside drawer
          },
        }}
      >
        {drawer} {/* Render the drawer content */}
      </Drawer>
    </>
  );
};